// import { useCallback, useEffect, useRef, useState } from "react";
// import UserService from "../../Services/UserService";
// import { listenUnreadMessagesByPatient } from "../../Services/chatService";
// import { normalizeDoctor } from "./chatHelpers";

// /**
//  * Subscribes to unread-message counts for a patient. Encapsulated so the
//  * main component doesn't need to know about chatService internals.
//  */
// export function useUnreadCounts(patientId) {
//   const [unreadCounts, setUnreadCounts] = useState({});

//   useEffect(() => {
//     if (!patientId) return;
//     const unsubscribe = listenUnreadMessagesByPatient(patientId, setUnreadCounts);
//     return () => unsubscribe();
//   }, [patientId]);

//   return unreadCounts;
// }

// /**
//  * Resolves a list of doctor IDs (from listenForDoctorsList) into full doctor
//  * records.
//  *
//  * The backend only supports filtering by a single userId at a time, so a
//  * batch network call isn't available — we still issue one request per
//  * doctor, but:
//  *   - in parallel (Promise.all) instead of sequential awaits in a loop
//  *   - cached by userId, so re-renders / re-subscriptions don't re-fetch
//  *     doctors we already resolved
//  *   - guarded against race conditions: if doctorIds changes again before
//  *     the in-flight batch resolves, the stale result is discarded
//  */
// export function useResolvedDoctors(doctorIds, fetchDoctorById) {
//   const [doctors, setDoctors] = useState([]);
//   const cacheRef = useRef(new Map()); // userId -> normalized doctor

//   useEffect(() => {
//     if (!doctorIds || doctorIds.length === 0) {
//       setDoctors([]);
//       return;
//     }

//     let cancelled = false;

//     const resolve = async () => {
//       const idsToFetch = doctorIds.filter((id) => !cacheRef.current.has(id));

//       if (idsToFetch.length > 0) {
//         const results = await Promise.all(
//           idsToFetch.map(async (id) => {
//             try {
//               return await fetchDoctorById(id);
//             } catch (e) {
//               console.error("[useResolvedDoctors] failed for", id, e);
//               return null;
//             }
//           })
//         );

//         results.forEach((doc, i) => {
//           if (doc) cacheRef.current.set(idsToFetch[i], normalizeDoctor(doc));
//         });
//       }

//       if (cancelled) return; // a newer doctorIds value superseded this run

//       const resolved = doctorIds
//         .map((id) => cacheRef.current.get(id))
//         .filter(Boolean);
//       setDoctors(resolved);
//     };

//     resolve();

//     return () => {
//       cancelled = true;
//     };
//   }, [doctorIds, fetchDoctorById]);

//   return doctors;
// }

// /**
//  * Paginated "all consulted doctors" list with infinite scroll support.
//  * Exposes a ref to attach to a sentinel element for IntersectionObserver.
//  */
// export function useConsultedDoctors(patientId, pageSize = 20) {
//   const [doctors, setDoctors] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [hasMore, setHasMore] = useState(true);
//   const pageRef = useRef(1);
//   const loadingRef = useRef(false);
//   const sentinelRef = useRef(null);

//   const loadPage = useCallback(
//     async (page, reset = false) => {
//       if (loadingRef.current || !patientId) return;
//       loadingRef.current = true;
//       setLoading(true);
//       setError(null);

//       try {
//         const response = await UserService.getConsultedDoctors({
//           patientId,
//           page,
//           pageSize,
//         });
//         const data = normalizeDoctorListSafe(response?.data);
//         setDoctors((prev) => (reset ? data : [...prev, ...data]));
//         setHasMore(data.length >= pageSize);
//         pageRef.current = page;
//       } catch (e) {
//         console.error("[useConsultedDoctors] load failed:", e);
//         setError("Could not load doctors. Please try again.");
//       } finally {
//         setLoading(false);
//         loadingRef.current = false;
//       }
//     },
//     [patientId, pageSize]
//   );

//   const reset = useCallback(() => {
//     pageRef.current = 1;
//     setHasMore(true);
//     loadPage(1, true);
//   }, [loadPage]);

//   useEffect(() => {
//     if (!hasMore) return;
//     const node = sentinelRef.current;
//     if (!node) return;

//     const observer = new IntersectionObserver(
//       (entries) => {
//         if (entries[0].isIntersecting && !loadingRef.current) {
//           loadPage(pageRef.current + 1);
//         }
//       },
//       { threshold: 0.1 }
//     );

//     observer.observe(node);
//     return () => observer.disconnect();
//   }, [hasMore, loadPage]);

//   return { doctors, loading, error, hasMore, sentinelRef, loadPage, reset };
// }

// function normalizeDoctorListSafe(list) {
//   return (list ?? []).map(normalizeDoctor).filter(Boolean);
// }

// version-2 with fixes

import { useCallback, useEffect, useRef, useState } from "react";
import UserService from "../../Services/UserService";
import { listenUnreadMessagesByPatient } from "../../Services/chatService";
import { normalizeDoctor } from "./chatHelpers";

/**
 * Subscribes to unread-message counts for a patient. Encapsulated so the
 * main component doesn't need to know about chatService internals.
 */
export function useUnreadCounts(patientId) {
  const [unreadCounts, setUnreadCounts] = useState({});

  useEffect(() => {
    if (!patientId) return;
    const unsubscribe = listenUnreadMessagesByPatient(patientId, setUnreadCounts);
    return () => unsubscribe();
  }, [patientId]);

  return unreadCounts;
}

/**
 * Resolves a list of doctor IDs (from listenForDoctorsList) into full doctor
 * records using a single batch API call.
 *
 * Now that the backend supports { userIds: [...], status: 1 }, we send one
 * request for all uncached IDs at once instead of N parallel per-ID calls.
 *
 * Still maintains:
 *   - a per-userId cache so re-renders / Firestore re-subscriptions never
 *     re-fetch doctors already resolved in this session
 *   - a race-condition guard: if doctorIds changes before an in-flight
 *     batch resolves, the stale result is silently discarded
 */
export function useResolvedDoctors(doctorIds) {
  const [doctors, setDoctors] = useState([]);
  const cacheRef = useRef(new Map()); // userId -> normalized doctor

  useEffect(() => {
    if (!doctorIds || doctorIds.length === 0) {
      setDoctors([]);
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      const idsToFetch = doctorIds.filter((id) => !cacheRef.current.has(id));

      if (idsToFetch.length > 0) {
        try {
          // Single batch call — backend now accepts an array of userIds.
          const response = await UserService.getDoctors({
            userIds: idsToFetch,
            status: 1,
          });
          const fetched = (response?.data || []).filter(
            // Keep only video-enabled doctors (userType "P" with a videoFee),
            // matching the filter the original per-ID getDoctorsList applied.
            // If your backend already guarantees this, this filter is harmless.
            (doc) => doc.userType === "P" && doc.videoFee > 0
          );
          fetched.forEach((doc) => {
            if (doc?.userId) {
              cacheRef.current.set(doc.userId, normalizeDoctor(doc));
            }
          });
        } catch (e) {
          console.error("[useResolvedDoctors] batch fetch failed:", e);
        }
      }

      if (cancelled) return; // a newer doctorIds value superseded this run

      const resolved = doctorIds
        .map((id) => cacheRef.current.get(id))
        .filter(Boolean);
      setDoctors(resolved);
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [doctorIds]);

  return doctors;
}

/**
 * Paginated "all consulted doctors" list with infinite scroll support.
 * Exposes a ref to attach to a sentinel element for IntersectionObserver.
 */
// export function useConsultedDoctors(patientId, pageSize = 20) {
//   const [doctors, setDoctors] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [hasMore, setHasMore] = useState(true);
//   const pageRef = useRef(1);
//   const loadingRef = useRef(false);
//   const sentinelRef = useRef(null);

//   const loadPage = useCallback(
//     async (page, reset = false) => {
//       if (loadingRef.current || !patientId) return;
//       loadingRef.current = true;
//       setLoading(true);
//       setError(null);

//       try {
//         const response = await UserService.getConsultedDoctors({
//           patientId,
//           page,
//           pageSize,
//         });
//         const data = normalizeDoctorListSafe(response?.data);
//         setDoctors((prev) => (reset ? data : [...prev, ...data]));
//         setHasMore(data.length >= pageSize);
//         pageRef.current = page;
//       } catch (e) {
//         console.error("[useConsultedDoctors] load failed:", e);
//         setError("Could not load doctors. Please try again.");
//       } finally {
//         setLoading(false);
//         loadingRef.current = false;
//       }
//     },
//     [patientId, pageSize]
//   );

//   const reset = useCallback(() => {
//     pageRef.current = 1;
//     setHasMore(true);
//     loadPage(1, true);
//   }, [loadPage]);

//   useEffect(() => {
//     if (!hasMore) return;
//     const node = sentinelRef.current;
//     if (!node) return;

//     const observer = new IntersectionObserver(
//       (entries) => {
//         if (entries[0].isIntersecting && !loadingRef.current) {
//           loadPage(pageRef.current + 1);
//         }
//       },
//       { threshold: 0.1 }
//     );

//     observer.observe(node);
//     return () => observer.disconnect();
//   }, [hasMore, loadPage]);

//   return { doctors, loading, error, hasMore, sentinelRef, loadPage, reset };
// }

//updated
/**
 * Paginated "all consulted doctors" list with infinite scroll support.
 * Accepts an optional search term — changing it resets to page 1 and
 * refetches, same as changing patientId.
 */
export function useConsultedDoctors(patientId, search = "", pageSize = 20) {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(1);
  const loadingRef = useRef(false);
  const sentinelRef = useRef(null);
  const searchRef = useRef(search);

  const loadPage = useCallback(
    async (page, reset = false) => {
      if (loadingRef.current || !patientId) return;
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const response = await UserService.getConsultedDoctors({
          patientId,
          page,
          pageSize,
          doctorName: searchRef.current || undefined,
        });
        const data = normalizeDoctorListSafe(response?.data);
        setDoctors((prev) => (reset ? data : [...prev, ...data]));
        setHasMore(data.length >= pageSize);
        pageRef.current = page;
      } catch (e) {
        console.error("[useConsultedDoctors] load failed:", e);
        setError("Could not load doctors. Please try again.");
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [patientId, pageSize]
  );

  const reset = useCallback(() => {
    pageRef.current = 1;
    setHasMore(true);
    loadPage(1, true);
  }, [loadPage]);

  // Re-fetch from page 1 whenever the search term changes.
  useEffect(() => {
    searchRef.current = search;
    if (!patientId) return;
    pageRef.current = 1;
    setHasMore(true);
    loadPage(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, search]);

  useEffect(() => {
    if (!hasMore) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingRef.current) {
          loadPage(pageRef.current + 1);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadPage]);

  return { doctors, loading, error, hasMore, sentinelRef, loadPage, reset };
}

function normalizeDoctorListSafe(list) {
  return (list ?? []).map(normalizeDoctor).filter(Boolean);
}